/**
 * CTO + Paid Social (task 069-01, epic 069 D1 — QA-20): the deliberate UI
 * union of the two style screens under ONE range + test-toggle bar, with the
 * compact totals strip above them. The tables are NOT merged — their source
 * grains differ (site vs `utm_source`) and CTO carries rate/cost columns PS
 * does not; each section keeps its own Campaign Select.
 *
 * Mechanism (the r1 B-1 / r2 H-1 controlled-props seam): this screen owns the
 * `useReportRange` + test-toggle state and passes `{ range, includeTest }`
 * down to both sections, which then suppress their own filter-bar range/test
 * controls and their `ReportShell` chrome. Standalone tabs never pass the
 * props and are untouched.
 *
 * The strip comes from `GET /admin/reports/cto-ps-totals` and is labeled
 * "All campaigns" (the r1 M-1 fold): it is scoped `from`/`to`/`includeTest`
 * only, so the per-section Campaign Selects narrow their tables, not the
 * strip — stated on the screen so the two never read as a contradiction. Its
 * cross-style unique is server-computed because uniques never add: an address
 * captured in both styles is one address.
 */

import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { fetchCtoPsTotals, type CtoPsStyle, type CtoPsTotalsResponse } from './cto-stats-api';
import { ReportShell } from './report-shell';
import { RangeFilters, TestLeadsToggle, useReportRange } from './report-filters';
import { ErrorBanner } from './report-table';
import { CtoStats } from './cto-stats';
import { PaidSocialStats } from './paid-social-stats';

/** The deduped cross-style cell's title — the reason the strip is server-computed. */
const BOTH_STYLES_UNIQUE_TITLE =
  'Distinct addresses across both styles — an address captured in both counts once';

export function CtoPaidSocial() {
  const range = useReportRange();
  const { from, to } = range;
  const [includeTest, setIncludeTest] = useState(false);
  const [totals, setTotals] = useState<CtoPsTotalsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch on every filter change; the stale response of a superseded filter
  // set is dropped rather than rendered (co-op-counts.tsx's idiom).
  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCtoPsTotals({ from, to, includeTest })
      .then((res) => {
        if (!cancelled) setTotals(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, includeTest]);

  const styleTotals = (style: CtoPsStyle) => totals?.styles.find((s) => s.style === style);

  const cto = styleTotals('cto_email');
  const paidSocial = styleTotals('paid_social');

  return (
    <ReportShell
      title="CTO + Paid Social"
      desc="Both styles under one range and test toggle. The totals strip is all campaigns — the section filters narrow their tables, not the strip."
    >
      <Card className="mb-4 px-4 py-3">
        {error && <ErrorBanner message={error} />}
        <div
          className="flex flex-wrap items-center gap-x-8 gap-y-3"
          data-testid="cto-ps-totals-strip"
        >
          <div className="min-w-[14rem]">
            <div className="text-xs font-semibold uppercase tracking-wide text-faint">
              All campaigns
            </div>
            <div className="text-xs text-faint">
              Section filters below narrow their tables, not this strip
            </div>
          </div>
          <div>
            <div className="text-xs text-faint">CTO Email leads</div>
            <div className="text-lg font-semibold tabular-nums">{cto?.leads ?? '…'}</div>
          </div>
          <div>
            <div className="text-xs text-faint">CTO Email unique</div>
            <div className="text-lg font-semibold tabular-nums">{cto?.uniqueLeads ?? '…'}</div>
          </div>
          <div>
            <div className="text-xs text-faint">Paid Social leads</div>
            <div className="text-lg font-semibold tabular-nums">{paidSocial?.leads ?? '…'}</div>
          </div>
          <div>
            <div className="text-xs text-faint">Paid Social unique</div>
            <div className="text-lg font-semibold tabular-nums">
              {paidSocial?.uniqueLeads ?? '…'}
            </div>
          </div>
          <div title={BOTH_STYLES_UNIQUE_TITLE}>
            <div className="text-xs text-faint">Unique, both styles</div>
            <div className="text-lg font-semibold tabular-nums">
              {totals ? totals.uniqueLeads : '…'}
            </div>
          </div>
          {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>
      </Card>

      {/* The one shared bar — the two sections suppress their own range/test controls. */}
      <div className="mb-4 flex flex-wrap items-center gap-3 border-b border-line px-1 py-2">
        <RangeFilters
          preset={range.preset}
          from={range.from}
          to={range.to}
          applyPreset={range.applyPreset}
          setRange={range.setRange}
          testIdPrefix="cto-ps"
        />
        <TestLeadsToggle checked={includeTest} onChange={setIncludeTest} />
      </div>

      <div className="mb-6">
        <CtoStats range={{ from, to }} includeTest={includeTest} />
      </div>
      <PaidSocialStats range={{ from, to }} includeTest={includeTest} />
    </ReportShell>
  );
}
